import {
    ChangeDetectorRef,
    Component,
    forwardRef,
    Input,
    OnInit,
    ChangeDetectionStrategy,
    inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { QueryObserverResult } from '@tanstack/query-core';
import { AsyncPipe } from '@angular/common';
import { BehaviorSubject, filter, Observable, switchMap, take } from 'rxjs';
import { LabelModule } from '@progress/kendo-angular-label';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { LocationDataService } from '../../services/location-data.service';
import { VirtualizationSettings } from '@progress/kendo-angular-dropdowns';

@Component({
    selector: 'app-subregions-filter',
    templateUrl: './subregions-filter.component.html',
    styleUrls: ['./subregions-filter.component.scss'],
    imports: [AsyncPipe, LabelModule, DropDownsModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SubRegionsFilterComponent),
            multi: true,
        },
    ],
})
export class SubRegionsFilterComponent implements ControlValueAccessor, OnInit {
    private locationDataService = inject(LocationDataService);
    private cdr = inject(ChangeDetectorRef);

    public subRegionsQueryResults$?: Observable<
        QueryObserverResult<string[], Error>
    >;

    public isDisabled: boolean = false;
    public value: string[] = [];
    public virtual: VirtualizationSettings = { itemHeight: 28 };

    private onChange: (value: string[]) => void = () => {};
    private onTouched: () => void = () => {};

    private regionsSubject = new BehaviorSubject<string[]>([]);

    @Input() set regions(value: string[]) {
        this.regionsSubject.next(value ?? []);
    }

    writeValue(value: string[]): void {
        this.value = value ?? [];
        this.cdr.markForCheck();
    }

    registerOnChange(fn: (value: string[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    handleValueChange(value: string[]): void {
        this.value = value;
        this.onChange(value);
    }

    handleBlur(): void {
        this.onTouched();
    }

    ngOnInit(): void {
        this.subRegionsQueryResults$ =
            this.locationDataService.dataUpdated$.pipe(
                filter((updated) => updated),
                take(1),
                switchMap(() => this.regionsSubject),
                switchMap(
                    (regions) =>
                        this.locationDataService.getDashboardFilterSubRegions(
                            '',
                            regions,
                        ).result$,
                ),
            );
    }
}
